import React from "react";
import { Menu, UserCog, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getInitials } from "@/lib/format";
import { toast } from "sonner";
import AccountSwitcher from "@/components/AccountSwitcher";
import ActionCenter from "@/components/ActionCenter";

export default function Topbar({ onMenuClick = () => {}, collapsed = false, onToggleCollapse = () => {} }) {
  const { user, account, viewAs, viewAsName } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const isAdmin = user.role === "admin";
  const isStaff = isAdmin || user.role === "returns_manager";
  const impersonating = isAdmin && viewAs && viewAs !== "all";

  const actAsCustomer = () => {
    if (impersonating) {
      navigate("/");
    } else {
      toast.info("Pick a client from the account switcher to act as them →");
    }
  };

  return (
    <header
      data-testid="app-topbar"
      className={`fixed top-0 left-0 ${collapsed ? "lg:left-0" : "lg:left-64"} right-0 h-20 bg-white border-b border-bh-divider z-30 flex items-center justify-between px-4 sm:px-8 gap-4 transition-[left] duration-200`}
      style={{ boxShadow: "0 1px 0 rgba(37, 55, 70, 0.04)" }}
    >
      <div className="flex items-center gap-2">
        <button
          data-testid="mobile-menu-btn"
          onClick={onMenuClick}
          className="lg:hidden p-2 -ml-1 hover:bg-bh-bg rounded-md transition-colors text-bh-navy-deep"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6" />
        </button>
        <button
          data-testid="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          className="hidden lg:inline-flex p-2 -ml-1 hover:bg-bh-bg rounded-md transition-colors text-bh-navy-deep"
          aria-label={collapsed ? "Open navigation" : "Collapse navigation"}
          title={collapsed ? "Open navigation" : "Collapse navigation"}
        >
          {collapsed ? <PanelLeftOpen className="w-6 h-6" /> : <PanelLeftClose className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex items-center justify-end gap-3 sm:gap-5 flex-1">
        {isStaff && <ActionCenter />}

        {isAdmin && (
          <button
            data-testid="act-as-customer-btn"
            onClick={actAsCustomer}
            className={`hidden sm:inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
              impersonating
                ? "bg-bh-navy-deep text-white hover:bg-bh-navy"
                : "bg-white border border-bh-divider text-bh-body hover:border-bh-orange hover:text-bh-orange"
            }`}
            title={impersonating ? `Open ${viewAsName || "the brand"}'s portal` : "Pick a client first"}
          >
            <UserCog className="w-4 h-4" />
            {impersonating ? "Act as customer" : "Act as customer"}
          </button>
        )}

        {isAdmin ? (
          <AccountSwitcher />
        ) : (
          <div data-testid="topbar-user" className="flex items-center gap-3 pl-3 sm:pl-5 sm:border-l border-bh-divider">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-bh-navy-deep leading-tight">{user.name}</p>
              <p className="text-xs text-bh-muted">{account?.company_name || "—"}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-bh-navy text-white flex items-center justify-center text-sm font-semibold">
              {getInitials(user.name)}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
